import {
  Box,
  Flex,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { convertToTimeOnly, getDayName } from "../utilities/DateUtil";
import { Message, ReadStatus } from "../models/Message";
import { FC, useEffect, useState } from "react";
import { fetchPresignedUrl } from "../utilities/FetchUtil";
import { FaFilePdf } from "react-icons/fa6";
import {
  FaCheck,
  FaCheckDouble,
  FaFile,
  FaFileAlt,
  FaFileImage,
} from "react-icons/fa";
import TextWithLinks from "./TextWithLinks";

interface ChatMessageProps {
  datesAreDifferent: boolean;
  index: number;
  isAdmin: boolean;
  message: Message;
}

const ChatMessage: FC<ChatMessageProps> = ({
  datesAreDifferent,
  message,
  index,
  isAdmin,
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileType, setFileType] = useState("");

  const theme = useMantineTheme();

  useEffect(() => {
    if (message.hashS3Key == null) return;
    const messageFileType =
      message.fileName?.endsWith("jpeg") ||
      message.fileName?.endsWith("jpg") ||
      message.fileName?.endsWith("png") ||
      message.fileName?.endsWith("gif")
        ? "image"
        : message.fileName?.endsWith("pdf")
          ? "pdf"
          : message.fileName?.endsWith("doc") ||
              message.fileName?.endsWith("docx")
            ? "document"
            : "misc";
    setFileType(messageFileType);
  }, [setFileType, message.fileName, message.hashS3Key]);

  const fetchFile = async (messageId: string | null) => {
    if (messageId == null) return;
    if (fileUrl) {
      window.open(fileUrl);
      return;
    }
    setIsLoading(true);
    const presignedUrl = await fetchPresignedUrl(messageId);
    const response = await fetch(presignedUrl);
    if (response.ok) {
      const blob = await response.blob();
      const newFileUrl = URL.createObjectURL(blob);
      setFileUrl(newFileUrl);
      if (fileType !== "image") {
        window.open(newFileUrl);
      }

      setIsLoading(false);
    } else {
      console.error(
        "Failed to fetch file using presigned url:",
        await response.text(),
      );
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        withCloseButton={false}
        size="auto"
        radius="md"
        overlayProps={{
          backgroundOpacity: 0.6,
          blur: 10,
        }}
        transitionProps={{
          transition: "fade",
          duration: 200,
        }}
        padding={0}
        centered
      >
        <Box h="100%" onClick={() => setOpened(false)}>
          <Image
            src={fileUrl}
            alt={"alt"}
            radius="md"
            mah="90vh"
            maw="90vw"
            style={{
              cursor: "zoom-out",
            }}
          />
        </Box>
      </Modal>

      {datesAreDifferent && (
        <Paper
          bg={theme.colors.gray[5]}
          radius="xl"
          shadow="sm"
          p="xs"
          style={{ alignSelf: "center" }}
          key={index + "dayName"}
        >
          <Text c="white" size="sm">
            {getDayName(message.isoCreatedAt)}
          </Text>
        </Paper>
      )}
      <Paper
        bg={isAdmin ? theme.colors.blue[5] : theme.colors.gray[6]}
        maw="75%"
        style={{
          alignSelf: isAdmin ? "flex-end" : "flex-start",
          wordBreak: "break-word",
        }}
        radius="lg"
        shadow="sm"
        key={index}
        p="sm"
      >
        <Text size="xs" c={theme.colors.gray[3]} mb="5px">
          {message.senderName}
        </Text>
        <TextWithLinks text={message.content} color="white" />
        <Flex justify="flex-end" mt="auto">
          <Stack align="flex-end">
            {isLoading ? (
              <Loader color="white" />
            ) : (
              // Attachments
              message.hashS3Key && (
                <>
                  {fileType === "image" && !fileUrl && (
                    // Images
                    <FaFileImage
                      className="file-icon"
                      color="white"
                      size="60"
                      onClick={() => fetchFile(message.messageId)}
                    />
                  )}
                  {fileType === "image" && fileUrl && (
                    // Image loaded
                    <Image
                      w="250px"
                      src={fileUrl}
                      radius="md"
                      onClick={() => setOpened(true)}
                    />
                  )}
                  {fileType === "pdf" ? (
                    // PDF
                    <FaFilePdf
                      className="file-icon"
                      color="white"
                      size="60"
                      onClick={() => fetchFile(message.messageId)}
                    />
                  ) : fileType === "document" ? (
                    // Assorted documents
                    <FaFileAlt
                      className="file-icon"
                      color="white"
                      size="60"
                      onClick={() => fetchFile(message.messageId)}
                    />
                  ) : fileType === "misc" ? (
                    // Any other type of file
                    <FaFile
                      className="file-icon"
                      color="white"
                      size="60"
                      onClick={() => fetchFile(message.messageId)}
                    />
                  ) : null}
                  <Text size="sm" c={theme.colors.gray[3]}>
                    {message.fileName ?? "No file name"}
                  </Text>
                </>
              )
            )}

            <Group>
              {message.messageId && isAdmin && (
                <>
                  {message.status === ReadStatus.READ ? (
                    <FaCheckDouble size="10" color="white" />
                  ) : (
                    <FaCheck size="10" color="white" />
                  )}
                </>
              )}
              <Text size="xs" c={theme.colors.gray[3]}>
                {convertToTimeOnly(message.isoCreatedAt)}
              </Text>
            </Group>
          </Stack>
        </Flex>
      </Paper>
    </>
  );
};

export default ChatMessage;
